import React, { useState, useEffect } from 'react';
import BiologicalLayout from '@/components/layout/BiologicalLayout';
import { useAuth, API } from '@/App';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { Clock, CheckCircle, Upload, ChevronDown, ChevronUp } from 'lucide-react';

const assessmentTypeLabels = {
  before_medicine: 'Before Medicine',
  after_medicine: 'After Medicine',
  transition: 'Transition Period',
  gait: 'Gait Analysis',
  posture: 'Posture Assessment',
  speech: 'Speech Assessment'
};

export default function SubscriberVideos() {
  const { user } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const [showReviewed, setShowReviewed] = useState(false);

  const subscriberId = user?.role === 'caregiver' ? user?.linked_subscriber_id : user?.id;

  useEffect(() => {
    const fetchAssessments = async () => {
      if (!subscriberId) return;
      try {
        const response = await axios.get(`${API}/video-assessments/${subscriberId}`);
        setAssessments(response.data);
      } catch (error) {
        console.error('Error fetching assessments:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAssessments();
  }, [subscriberId]);

  const handleFileUpload = async (assessmentId, file) => {
    if (!file) return;
    setUploading(prev => ({ ...prev, [assessmentId]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/video-assessments/${assessmentId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Video uploaded');
      const response = await axios.get(`${API}/video-assessments/${subscriberId}`);
      setAssessments(response.data);
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(prev => ({ ...prev, [assessmentId]: false }));
    }
  };

  const pendingAssessments = assessments.filter(a => a.status === 'pending');
  const uploadedAssessments = assessments.filter(a => a.status === 'uploaded');
  const reviewedAssessments = assessments.filter(a => a.status === 'reviewed');

  if (loading) {
    return (
      <BiologicalLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-bio-accent/30 border-t-bio-accent rounded-full animate-spin" />
        </div>
      </BiologicalLayout>
    );
  }

  return (
    <BiologicalLayout>
      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10 sm:py-14" data-testid="videos-page">

        {/* Title */}
        <div className="mb-14">
          <p className="text-[11px] uppercase tracking-[0.3em] text-bio-faint mb-3 font-['Work_Sans']"
             data-testid="videos-label">
            Visual Observations
          </p>
          <h1 className="text-3xl sm:text-4xl font-['Outfit'] font-light text-bio-primary leading-tight"
              data-testid="videos-title">
            Video Recordings
          </h1>
        </div>

        {/* Opening */}
        <section className="mb-12" data-testid="videos-opening">
          <p className="text-base text-bio-secondary leading-[1.8] font-['Work_Sans'] font-light max-w-2xl">
            {assessments.length > 0
              ? 'Your care team has requested visual observations. These recordings help assess movement patterns and response quality.'
              : 'When your care team needs to observe specific patterns, they will request recordings here.'}
          </p>
        </section>

        {/* Pending Uploads */}
        {pendingAssessments.length > 0 && (
          <section className="mb-14" data-testid="videos-pending">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-5 font-['Work_Sans']">
              Requested Recordings
            </p>
            <div className="space-y-6">
              {pendingAssessments.map((assessment) => (
                <div key={assessment.id}
                     className="rounded-2xl p-6 transition-all"
                     style={{ backgroundColor: 'rgba(140, 107, 85, 0.06)', border: '1px solid rgba(140, 107, 85, 0.15)' }}
                     data-testid={`pending-${assessment.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-bio-strong text-sm font-['Work_Sans'] font-medium">
                        {assessmentTypeLabels[assessment.assessment_type] || assessment.assessment_type}
                      </p>
                      <p className="text-[11px] text-bio-ghost font-['Work_Sans'] mt-1">
                        Requested {new Date(assessment.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-bio-accent font-['Work_Sans']">Awaiting</span>
                  </div>

                  {assessment.instructions && (
                    <p className="text-sm text-bio-secondary font-['Work_Sans'] leading-relaxed mb-4 pl-4 border-l-2 border-bio-accent/20">
                      {assessment.instructions}
                    </p>
                  )}

                  <div className="flex items-center gap-3">
                    <label className="cursor-pointer py-2.5 px-5 rounded-xl text-sm font-['Work_Sans'] font-medium flex items-center gap-2 transition-all hover:opacity-90"
                           style={{ backgroundColor: '#8C6B55', color: '#FBFaf9' }}>
                      <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
                      Select Video
                      <Input
                        type="file"
                        accept="video/*"
                        onChange={(e) => handleFileUpload(assessment.id, e.target.files?.[0])}
                        className="hidden"
                        disabled={uploading[assessment.id]}
                        data-testid={`upload-${assessment.id}`}
                      />
                    </label>
                    {uploading[assessment.id] && (
                      <div className="w-5 h-5 border-2 border-bio-accent/30 border-t-bio-accent rounded-full animate-spin" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Under Review */}
        {uploadedAssessments.length > 0 && (
          <section className="mb-12" data-testid="videos-review">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-5 font-['Work_Sans']">
              Under Review
            </p>
            <div className="space-y-3">
              {uploadedAssessments.map((a) => (
                <div key={a.id} className="flex items-center justify-between py-3" data-testid={`review-${a.id}`}>
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-bio-subtle" strokeWidth={1.5} />
                    <div>
                      <p className="text-sm text-bio-body font-['Work_Sans']">
                        {assessmentTypeLabels[a.assessment_type] || a.assessment_type}
                      </p>
                      <p className="text-[11px] text-bio-ghost font-['Work_Sans']">
                        Uploaded {new Date(a.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-[#5C6B8A] font-['Work_Sans']">Reviewing</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Reviewed */}
        {reviewedAssessments.length > 0 && (
          <section className="mb-10" data-testid="videos-reviewed">
            <button
              onClick={() => setShowReviewed(!showReviewed)}
              className="flex items-center gap-2 text-bio-subtle text-sm font-['Work_Sans'] hover:text-bio-body transition-colors"
              data-testid="reviewed-toggle"
            >
              {showReviewed ? <ChevronUp className="w-4 h-4" strokeWidth={1.5} /> : <ChevronDown className="w-4 h-4" strokeWidth={1.5} />}
              Reviewed recordings ({reviewedAssessments.length})
            </button>
            {showReviewed && (
              <div className="mt-5 space-y-5">
                {reviewedAssessments.map((a) => (
                  <div key={a.id} className="pb-5 border-b border-bio-border last:border-0" data-testid={`reviewed-${a.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-[#4E7A6B]" strokeWidth={1.5} />
                        <p className="text-sm text-bio-body font-['Work_Sans']">
                          {assessmentTypeLabels[a.assessment_type] || a.assessment_type}
                        </p>
                      </div>
                      <p className="text-[11px] text-bio-ghost font-['Work_Sans']">
                        {new Date(a.reviewed_at).toLocaleDateString()}
                      </p>
                    </div>
                    {a.review_notes && (
                      <p className="text-sm text-bio-secondary font-['Work_Sans'] leading-relaxed ml-5.5 pl-4 border-l-2 border-[#4E7A6B]/20">
                        {a.review_notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Empty State */}
        {assessments.length === 0 && (
          <section className="text-center py-10" data-testid="videos-empty">
            <p className="text-bio-faint text-sm font-['Work_Sans'] leading-relaxed max-w-md mx-auto">
              No recordings have been requested yet. Your care team will ask for visual observations when they are helpful for understanding your patterns.
            </p>
          </section>
        )}

        <div className="pt-4 pb-4">
          <div className="w-8 h-px bg-bio-border mx-auto" />
        </div>
      </div>
    </BiologicalLayout>
  );
}
